// Lead Detail — left panel with Main / Statistics / Setup tabs.
// Main tab supports per-field inline editing via the `InlineField` component.
import React, { Component } from "react";
import HelperClass from "../../../../config/helperClass";
import { updateLead, listPartnerProducts } from "../api/leads_api";
import { listAgents } from "../../../../config/api_calls";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Popper from "@material-ui/core/Popper";
import { fmtMoney, safeJson, daysSince } from "./helpers";

const auth = new HelperClass();

// Field schema for the Main tab.
const INDUSTRIES = [
  "Telecom", "Healthcare", "Logistics", "Retail", "Airport", "Education", "Mining",
  "Financial Services", "Manufacturing", "Hospitality", "Government", "Other",
];
const INQUIRY_TYPES = ["Service Inquiry", "Pricing", "Demo", "Partnership", "Support", "Other"];
const GEOGRAPHIES = ["North America", "South America", "EMEA", "APAC", "Other"];
const SOURCES = [
  "Direct", "Partner Referral", "Inbound Inquiry", "Outbound",
  "Cold Outreach", "LinkedIn Outreach", "Webinar", "Trade Show", "Other",
];
const PRIORITIES = [
  { value: "high", label: "High" },
  { value: "med",  label: "Medium" },
  { value: "low",  label: "Low" },
];

const MAIN_FIELDS = [
  { key: "company",          label: "Company",       type: "text" },
  { key: "contact_name",     label: "Contact",       type: "text" },
  { key: "email",            label: "Email",         type: "email" },
  { key: "phone",            label: "Phone",         type: "text" },
  { key: "job_title",        label: "Job Title",     type: "text" },
  { key: "inquiry_type",     label: "Inquiry Type",  type: "select", options: INQUIRY_TYPES },
  { key: "industry",         label: "Industry",      type: "select", options: INDUSTRIES },
  { key: "geography",        label: "Geography",     type: "select", options: GEOGRAPHIES },
  { key: "country",          label: "Country",       type: "text" },
  { key: "website",          label: "Website",       type: "text" },
  { key: "source",           label: "Source",        type: "select", options: SOURCES },
  { key: "partner",          label: "Partner",       type: "partner" },
  { key: "partner_contact",  label: "Partner Rep",   type: "text" },
  { key: "resp_user",        label: "Resp. User",    type: "text" },
  { key: "next_action_date", label: "Next Action",   type: "date" },
  { key: "priority",         label: "Priority",      type: "priority" },
];
const WIDE_FIELDS = [
  { key: "address",       label: "Address",             type: "textarea" },
  { key: "tags",          label: "Tags",                type: "tags" },
  { key: "project_info",  label: "Project Information", type: "textarea" },
  { key: "notes",         label: "Internal Notes",      type: "textarea" },
];

class InlineField extends Component {
  state = { editing: false, value: "", saving: false };

  enter = () => {
    const { lead, field } = this.props;
    let v = lead[field.key];
    if (v == null) v = "";
    if (field.type === "tags") v = JSON.stringify(safeJson(v, []) || []);
    this.setState({ editing: true, value: String(v) });
  };

  cancel = () => this.setState({ editing: false, value: "" });

  save = async () => {
    const { lead, field, onSaved } = this.props;
    this.setState({ saving: true });
    let v = this.state.value;
    if (field.type === "tags") {
      // user-entered comma-separated string → array
      v = v.split(",").map(function (s) { return s.trim(); }).filter(Boolean);
    }
    if (field.type === "number") v = v === "" ? 0 : Number(v);
    const payload = { lead_id: lead.id };
    payload[field.key] = field.type === "tags" ? JSON.stringify(v) : v;
    const r = await updateLead(auth.getAccount(), auth.getToken(), payload);
    this.setState({ saving: false, editing: false });
    if (r && r.data && r.data.status === 200 && onSaved) onSaved(field.key, field.type === "tags" ? v : payload[field.key]);
  };

  renderInput() {
    const { field } = this.props;
    const { value } = this.state;
    const setVal = (v) => this.setState({ value: v });
    if (field.type === "textarea") {
      return (
        <textarea className="ld-field-textarea" value={value}
          onChange={(e) => setVal(e.target.value)} autoFocus />
      );
    }
    if (field.type === "select") {
      return (
        <select className="ld-field-select" value={value} autoFocus
          onChange={(e) => setVal(e.target.value)}>
          <option value="">— None —</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (field.type === "priority") {
      return (
        <select className="ld-field-select" value={value} autoFocus
          onChange={(e) => setVal(e.target.value)}>
          {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      );
    }
    if (field.type === "date") {
      return (
        <input className="ld-field-input" type="date" value={value ? String(value).substring(0, 10) : ""}
          autoFocus onChange={(e) => setVal(e.target.value)} />
      );
    }
    return (
      <input className="ld-field-input" type={field.type === "number" ? "number" : (field.type === "email" ? "email" : "text")}
        value={value} autoFocus onChange={(e) => setVal(e.target.value)} />
    );
  }

  renderValue() {
    const { lead, field } = this.props;
    const v = lead[field.key];
    if (field.type === "tags") {
      const arr = safeJson(v, []) || [];
      if (!arr.length) return <span className="ld-field-val-empty">No tags</span>;
      return <span>{arr.map((t, i) => <span className="ld-chip" key={i}>{t}</span>)}</span>;
    }
    if (v == null || v === "") return <span className="ld-field-val-empty">—</span>;
    if (field.type === "number") return <span>{(field.prefix || "") + Number(v).toLocaleString()}</span>;
    if (field.type === "date") return <span>{String(v).substring(0, 10)}</span>;
    if (field.type === "priority") {
      const p = PRIORITIES.filter(function (x) { return x.value === v; })[0];
      return <span>{p ? p.label : v}</span>;
    }
    return <span>{String(v)}</span>;
  }

  render() {
    const { field } = this.props;
    const { editing, saving } = this.state;
    const wide = ["textarea", "tags"].indexOf(field.type) !== -1;
    return (
      <div className={"ld-field" + (wide ? " ld-field-wide" : "")} onClick={!editing ? this.enter : undefined}
        style={{ cursor: editing ? "default" : "pointer" }}>
        <div className="ld-field-lbl">{field.label}</div>
        {editing ? (
          <div>
            {this.renderInput()}
            <div className="ld-field-actions">
              <button className="ld-field-btn ld-field-btn-save" onClick={this.save} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button className="ld-field-btn ld-field-btn-cancel" onClick={this.cancel} disabled={saving}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="ld-field-row">
            <span className="ld-field-val">{this.renderValue()}</span>
            <span className="ld-field-pencil">✎</span>
          </div>
        )}
      </div>
    );
  }
}

function ReadField({ label, value }) {
  return (
    <div className="ld-field">
      <div className="ld-field-lbl">{label}</div>
      <div className="ld-field-val">{value == null || value === "" ? <span className="ld-field-val-empty">—</span> : value}</div>
    </div>
  );
}

class PartnerField extends Component {
  state = { editing: false, saving: false };

  enter = () => {
    this.setState({ editing: true });
  };

  cancel = () => {
    this.setState({ editing: false });
  };

  save = async (option) => {
    const { lead, onSaved } = this.props;
    this.setState({ saving: true });
    try {
      const partner_accountno = option ? option.value : "";
      const r = await updateLead(auth.getAccount(), auth.getToken(), {
        lead_id: lead.id,
        partner_accountno,
      });
      this.setState({ saving: false, editing: false });
      if (r && r.data && r.data.status === 200 && onSaved) {
        onSaved("partner_accountno", partner_accountno);
      }
    } catch (_) {
      this.setState({ saving: false, editing: false });
    }
  };

  render() {
    const { lead, partners, partnersLoading } = this.props;
    const { editing, saving } = this.state;
    const currentPartner = partners.find((p) => String(p.value) === String(lead.partner_accountno)) || null;
    const displayValue = currentPartner ? currentPartner.label : "—";

    return (
      <div className="ld-field" onClick={!editing ? this.enter : undefined}
        style={{ cursor: editing ? "default" : "pointer" }}>
        <div className="ld-field-lbl">Partner</div>
        {editing ? (
          <div>
            <Autocomplete
              options={partners}
              getOptionLabel={(option) => option.label || ""}
              value={currentPartner}
              loading={partnersLoading}
              openOnFocus
              noOptionsText={partnersLoading ? "Loading partners…" : "No partners found"}
              onChange={(event, newValue) => this.save(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder={partnersLoading ? "Loading partners…" : "Select partner"}
                  autoFocus
                />
              )}
            />
            <div className="ld-field-actions">
              <button className="ld-field-btn ld-field-btn-cancel" onClick={this.cancel} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="ld-field-row">
            <span className="ld-field-val">{displayValue}</span>
            <span className="ld-field-pencil">✎</span>
          </div>
        )}
      </div>
    );
  }
}

const BottomPopper = (props) => (
  <Popper {...props} placement="bottom-start" modifiers={{ flip: { enabled: false } }} />
);

class ProductsField extends Component {
  state = { editing: false, products: [], partnerProducts: [], productSearch: "", loadingPartnerProducts: false, saving: false };

  enter = async () => {
    const { lead, products, partner } = this.props;
    const curr = Array.isArray(products) ? products : [];
    this.setState({ editing: true, products: curr.slice(), productSearch: "" });
    const acct = partner ? (partner.accountno || partner.accountNo || partner.account) : (lead ? lead.partner_accountno : null);
    if (!acct) return;
    this.setState({ loadingPartnerProducts: true });
    try {
      const r = await listPartnerProducts(auth.getAccount(), auth.getToken(), acct);
      if (r && r.data && r.data.status === 200) {
        this.setState({ partnerProducts: r.data.data || [] });
      }
    } catch (_) {}
    this.setState({ loadingPartnerProducts: false });
  };

  cancel = () => this.setState({ editing: false, products: [], productSearch: "" });

  addProduct = (svc) => {
    this.setState((s) => {
      const products = [ ...(s.products || []), { service_id: svc.service_id, service_title: svc.service_title, rate: svc.rate } ];
      return { products };
    });
  };

  removeProductAt = (index) => {
    this.setState((s) => ({ products: (s.products || []).filter((_, i) => i !== index) }));
  };

  save = async () => {
    const { lead, onSaved } = this.props;
    this.setState({ saving: true });
    const products = this.state.products || [];
    const deal_value = products.reduce(function(sum, p) { return sum + (parseFloat(p && p.rate) || 0); }, 0);
    const payload = {
      lead_id: lead.id,
      products: JSON.stringify(products),
      deal_value: deal_value > 0 ? deal_value.toFixed(2) : 0,
      partner_accountno: lead.partner_accountno || "",
    };
    try {
      const r = await updateLead(auth.getAccount(), auth.getToken(), payload);
      this.setState({ saving: false, editing: false });
      if (r && r.data && r.data.status === 200) {
        if (onSaved) onSaved("products", this.state.products || []);
      }
    } catch (e) {
      this.setState({ saving: false });
    }
  };

  render() {
    const { products } = this.props;
    const { editing, partnerProducts, productSearch, loadingPartnerProducts, saving } = this.state;
    const display = Array.isArray(products) ? products : [];
    return (
      <div className="ld-field ld-field-wide" onClick={!editing ? this.enter : undefined}
        style={{ cursor: editing ? "default" : "pointer" }}>
        <div className="ld-field-lbl">Products of Interest</div>
        {editing ? (
          <div>
            <div className="ld-field-val" style={{ marginBottom: 8 }}>
              {(this.state.products || []).length === 0 ? (
                <span className="ld-field-val-empty">No products attached</span>
              ) : (
                (this.state.products || []).map((p, idx) => (
                  <span className="ld-chip" key={p.service_id + "-" + idx} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    {p.service_title}
                    <span onClick={() => this.removeProductAt(idx)} style={{ cursor: "pointer", fontWeight: 700, lineHeight: 1 }} title="Remove">×</span>
                  </span>
                ))
              )}
            </div>
            <div style={{ marginBottom: 10 }}>
              <Autocomplete
                options={partnerProducts.map((svc) => ({ id: svc.service_id, label: svc.service_title, svc }))}
                getOptionLabel={(option) => option.label || ""}
                getOptionSelected={() => false}
                value={null}
                inputValue={productSearch}
                onInputChange={(e, newInput, reason) => {
                  if (reason === "input") this.setState({ productSearch: newInput });
                  else if (reason === "clear") this.setState({ productSearch: "" });
                }}
                onChange={(e, newValue) => {
                  if (newValue && newValue.svc) {
                    this.addProduct(newValue.svc);
                    this.setState({ productSearch: "" });
                  }
                }}
                disableCloseOnSelect
                clearOnBlur={false}
                clearOnEscape={false}
                noOptionsText={loadingPartnerProducts ? "Loading…" : "No products"}
                PopperComponent={BottomPopper}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" placeholder="Search & select products…" inputProps={{ ...params.inputProps, autoComplete: "new-password" }} />
                )}
              />
            </div>
            <div className="ld-field-actions">
              <button className="ld-field-btn ld-field-btn-save" onClick={this.save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
              <button className="ld-field-btn ld-field-btn-cancel" onClick={this.cancel} disabled={saving}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="ld-field-row">
            <span className="ld-field-val">
              {display.length === 0 ? <span className="ld-field-val-empty">No products attached</span> : display.map((p, i) => <span className="ld-chip" key={(p.id||p.service_id||i)}>{p.service_title}</span>)}
            </span>
            <span className="ld-field-pencil">✎</span>
          </div>
        )}
      </div>
    );
  }
}

export default class LeftPanel extends Component {
  state = { tab: "main", partners: [], partnersLoading: false };

  componentDidMount() {
    this.loadPartners();
  }

  normalizePartner = (partnerRow) => {
    if (!partnerRow) return null;
    if (Array.isArray(partnerRow)) {
      const accountno = String(partnerRow[8] || "").trim();
      if (!accountno) return null;
      const name = String(partnerRow[1] || "").trim();
      const extra = String(partnerRow[2] || "").trim();
      const label = [name, extra].filter(Boolean).join(" ") || accountno;
      return { value: accountno, label, isActive: partnerRow[5] === "active__Yes" };
    }
    const accountno = String(partnerRow.accountno || partnerRow.accountNo || partnerRow.account || partnerRow.id || partnerRow.value || "").trim();
    const label = partnerRow.comp_name || partnerRow.name || partnerRow.partner_name || partnerRow.label || partnerRow.company || accountno;
    return { value: accountno, label };
  };

  loadPartners = async () => {
    this.setState({ partnersLoading: true });
    try {
      const r = await listAgents(auth.getAccount(), auth.getToken());
      if (r && r.data && r.data.status === 200) {
        const partners = (Array.isArray(r.data.data) ? r.data.data : []).map(this.normalizePartner).filter(Boolean);
        this.setState({ partners });
      }
    } catch (_) {
      // ignore partner list load errors silently
    }
    this.setState({ partnersLoading: false });
  };



  setTab = (t) => this.setState({ tab: t });

  renderMain() {
    const { lead, partner, products, onFieldSaved } = this.props;
    const hasSharelink = !!(lead && lead.sharelink_title && lead.sharelink_complete_link);
    if (lead) {
      console.log("Lead data - link_identifier:", lead.link_identifier);
      console.log("Lead data - sharelink_title:", lead.sharelink_title);
      console.log("Lead data - sharelink_complete_link:", lead.sharelink_complete_link);
      console.log("hasSharelink condition:", hasSharelink);
    }
    const leadWithDerived = Object.assign({}, lead, {
      // No-op; placeholder if we later compute derived display values.
    });
    return (
      <div>
        {hasSharelink && (
          <div className="ld-stats-summary-card" style={{ marginBottom: "15px" }}>
            <div>
              <div className="ld-stats-summary-label">Sharelink</div>
              <div style={{ marginTop: "8px" }}>
                <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "6px" }}>
                  {lead.sharelink_title}
                </div>
                <a
                  href={lead.sharelink_complete_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: "12px",
                    color: "#1976d2",
                    textDecoration: "none",
                    wordBreak: "break-all",
                  }}
                >
                  {lead.sharelink_complete_link}
                </a>
              </div>
            </div>
          </div>
        )}
        <div className="ld-fields">
          {MAIN_FIELDS.map((f) => {
            if (f.key === "partner") {
              return (
                <PartnerField
                  key={f.key}
                  lead={lead}
                  partners={this.state.partners}
                  partnersLoading={this.state.partnersLoading}
                  onSaved={onFieldSaved}
                />
              );
            }
            return <InlineField key={f.key} field={f} lead={leadWithDerived} onSaved={onFieldSaved} />;
          })}
          <ReadField label="Deal Value" value={fmtMoney(lead.deal_value)} />
          <ReadField label="Stage" value={lead.stage_label || lead.stage_key} />
        </div>
        <div className="ld-fields-wide">
          {WIDE_FIELDS.map((f) => (
            <InlineField key={f.key} field={f} lead={leadWithDerived} onSaved={onFieldSaved} />
          ))}
          <ProductsField lead={lead} partner={partner} products={products} onSaved={onFieldSaved} />
        </div>
      </div>
    );
  }

  renderStats() {
    const { lead } = this.props;
    const days = daysSince(lead.created_at);
    return (
      <div>
        <div className="ld-stats-summary-card">
          <div>
            <div className="ld-stats-summary-label">Days Active</div>
            <div className="ld-stats-summary-value">{lead.created_at ? (days != null ? days : "0") : "—"}</div>
          </div>
        </div>
        <div className="ld-fields">
          <ReadField label="Score" value={lead.score} />
          <ReadField label="Deal Value" value={fmtMoney(lead.deal_value)} />
          <ReadField label="Stage" value={lead.stage_key} />
          <ReadField label="Priority" value={lead.priority} />
          <ReadField label="Source" value={lead.source} />
          <ReadField label="Next Action" value={lead.next_action_date ? String(lead.next_action_date).substring(0, 10) : null} />
        </div>
      </div>
    );
  }

  renderSetup() {
    const { lead, partner, tracking } = this.props;
    const t = tracking || {};
    const hasLatLng = t.latitude != null && t.latitude !== "" && t.longitude != null && t.longitude !== "";
    return (
      <div>
        <div className="ld-fields">
          <ReadField label="Lead UID" value={lead.lead_uid} />
          <ReadField label="Status" value={lead.status} />
          <ReadField label="Created" value={lead.created_at ? new Date(lead.created_at).toLocaleString() : null} />
          <ReadField label="Updated" value={lead.updated_at ? new Date(lead.updated_at).toLocaleString() : null} />
          <ReadField label="Won At" value={lead.won_at} />
          <ReadField label="Lost At" value={lead.lost_at} />
          <ReadField label="Created By" value={lead.created_by} />
          <ReadField label="Partner Acct." value={partner ? partner.accountno : lead.partner_accountno} />
          <ReadField label="Score Source" value={lead.score_source || "formula"} />
        </div>

        <div className="ld-stats-summary-card" style={{ marginTop: 14, marginBottom: 4 }}>
          <div><div className="ld-stats-summary-label">Location Information</div></div>
        </div>
        <div className="ld-fields">
          <ReadField label="IP Address" value={t.ip_address} />
          <ReadField label="Country" value={t.country} />
          <ReadField label="Region/State" value={t.region} />
          <ReadField label="City" value={t.city} />
          <ReadField label="Postal Code" value={t.postal_code} />
          <ReadField label="Timezone" value={t.timezone} />
          {hasLatLng && <ReadField label="Latitude / Longitude" value={t.latitude + ", " + t.longitude} />}
        </div>

        <div className="ld-stats-summary-card" style={{ marginTop: 14, marginBottom: 4 }}>
          <div><div className="ld-stats-summary-label">Device Information</div></div>
        </div>
        <div className="ld-fields">
          <ReadField label="Device Type" value={t.device_type} />
          <ReadField label="Operating System" value={t.operating_system} />
          <ReadField label="Browser Name" value={t.browser_name} />
          <ReadField label="Browser Version" value={t.browser_version} />
          <ReadField label="Device Model" value={t.device_model} />
        </div>
        <div className="ld-fields-wide">
          <div className="ld-field ld-field-wide">
            <div className="ld-field-lbl">User Agent</div>
            <div className="ld-field-val" style={{ wordBreak: "break-all" }}>
              {t.user_agent == null || t.user_agent === ""
                ? <span className="ld-field-val-empty">—</span>
                : t.user_agent}
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { tab } = this.state;
    return (
      <aside className="ld-side ld-side-left">
        <div className="ld-left-panel">
          <div className="ld-side-tabs" style={{ margin: "10px 10px 0", flexShrink: 0 }}>
            <button className={"ld-tab" + (tab === "main"  ? " ld-tab-on" : "")} onClick={() => this.setTab("main")}>Main</button>
            <button className={"ld-tab" + (tab === "stats" ? " ld-tab-on" : "")} onClick={() => this.setTab("stats")}>Statistics</button>
            <button className={"ld-tab" + (tab === "setup" ? " ld-tab-on" : "")} onClick={() => this.setTab("setup")}>Setup</button>
          </div>
          <div className="ld-left-panel-scroll">
            {tab === "main" ? this.renderMain() : tab === "stats" ? this.renderStats() : this.renderSetup()}
          </div>
        </div>
      </aside>
    );
  }
}
